/**
 * useRevokeInvitation — TanStack mutation over
 * `POST /admin/staff/invitations/:invitationId/revoke` (no body).
 *
 * Error envelope (tagged union):
 *   - 'alreadyAccepted' — 422 (the invitation was already accepted).
 *   - 'notFound'        — 404 (no such invitation).
 *   - 'forbidden'       — 403 (caller is not root).
 *   - 'error'           — anything else.
 *
 * On success, invalidates the canonical invitations key so the revoked
 * invitation row disappears.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { STAFF_INVITATIONS_QUERY_KEY } from './use-staff-invitations.js';

export type RevokeInvitationError =
  | { readonly kind: 'alreadyAccepted' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error' };

export interface UseRevokeInvitationResult {
  readonly mutateAsync: (invitationId: string) => Promise<null>;
  readonly isPending: boolean;
}

export function useRevokeInvitation(): UseRevokeInvitationResult {
  const queryClient = useQueryClient();

  // Note: `useMutation` does not accept `void` as its first generic
  // because lint forbids `void` outside return types. Use `null` and
  // resolve with it on success — call sites await without consuming
  // the value.
  const mutation = useMutation<null, RevokeInvitationError, string>({
    mutationFn: async (invitationId) => {
      const { response } = await apiClient.POST(
        '/admin/staff/invitations/{invitationId}/revoke',
        { params: { path: { invitationId } } },
      );
      if (response.status === 422) {
        throw { kind: 'alreadyAccepted' } satisfies RevokeInvitationError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies RevokeInvitationError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies RevokeInvitationError;
      }
      if (!response.ok) {
        throw { kind: 'error' } satisfies RevokeInvitationError;
      }
      return null;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STAFF_INVITATIONS_QUERY_KEY,
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
